// src/modules/app-views/dataSearch/pages/Search/components/SearchedEntity.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import {
  Flex,
  Typography,
  Divider,
  Descriptions,
  Alert,
  Tooltip,
  Popover,
  Checkbox,
  Button,
  Collapse,
  Tag,
  Grid,
  theme,
} from 'antd';
import {
  ShopOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  AimOutlined,
  BankOutlined,
  GlobalOutlined,
  WhatsAppOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  AuditOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { LiaFaxSolid } from 'react-icons/lia';
import translateX from 'utils/translateX';
import { toCapitalCase } from 'utils/helpers';
import TypingText from './TypingText';
import DataFreshnessAlert from './DataFreshnessAlert';
import {
  buildNaturalIntro,
  formatCnpj,
  formatCpf,
  formatCep,
  formatPhone,
  formatPrice,
} from '../helpers';
import {
  HOLDER_TYPE,
  HOLDER_TYPE_SHORT,
  ESTABLISHMENT_TYPE,
  ESTABLISHMENT_TYPE_LABELS,
  REGISTRATION_STATUS,
  REGISTRATION_STATUS_LABELS,
  isHolderTypePerson,
  isRegistrationActive,
  isHeadquarters,
} from 'constants/DataSearchConstants';
import { CRT_TAG_CONFIG } from 'services/fiscalService';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Profile options for entity creation
const ENTITY_PROFILE_OPTIONS = [
  { value: 'company', label: 'my_company' },
  { value: 'customer', label: 'customer' },
  { value: 'supplier', label: 'supplier' },
  { value: 'collaborator', label: 'collaborator' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SearchedEntity - Displays the searched entity details with typing effect
 */
const SearchedEntity = ({
  message,
  typingProgress,
  introTypingComplete,
  chatContainerRef,
  creditsInfo = {},
  searchDataAge,
  onIntroComplete,
  onUpdateEntity,
  onCreateEntity,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Mobile detection: true when screen is xs or sm (< 768px)
  const isMobile = !screens.md;

  const {
    entity,
    meta,
    entityExists,
    existingEntityId,
    existingEntityProfile,
    engine,
    resolvedProvider,
  } = message;
  const tp = typingProgress; // shorthand for typing progress
  const isCompany = entity?.entityType === 'company';

  // Generate natural language intro (include profiles if entity exists)
  const naturalIntro = buildNaturalIntro(entity, entityExists, existingEntityProfile);

  // Get primary document (CNPJ or CPF)
  const cnpjDoc = entity?.documents?.find(d => d.documentType === 'cnpj');
  const cpfDoc = entity?.documents?.find(d => d.documentType === 'cpf');

  // Get contacts by type
  const phones = entity?.contacts?.filter(c => c.contactType === 'phone') || [];
  const emails = entity?.contacts?.filter(c => c.contactType === 'email') || [];
  const faxes = entity?.contacts?.filter(c => c.contactType === 'fax') || [];

  // Get primary address
  const primaryAddress = entity?.addresses?.find(a => a.isPrimary) || entity?.addresses?.[0];

  // Get activities
  const mainActivity = entity?.activities?.find(a => a.isPrimary) || entity?.mainActivity;
  const secondaryActivities =
    entity?.activities?.filter(a => !a.isPrimary) || entity?.additionalActivities || [];

  // Get registration status
  const registrationStatus = entity?.registrationStatus;
  // Support both new ENUM format and legacy numeric/text format
  const isActive =
    registrationStatus?.status === REGISTRATION_STATUS.ACTIVE || // New: 'active'
    registrationStatus?.status === 'active' || // String fallback
    registrationStatus?.statusCode === 2 || // Legacy numeric code
    registrationStatus?.statusRaw?.toUpperCase() === 'ATIVA'; // Raw text fallback

  // Get fiscal data
  const fiscalData = entity?.fiscalData;

  // Get state registrations (Inscricoes Estaduais)
  const stateRegistrations = entity?.stateRegistrations || [];

  // Get establishment type and CNPJ structure
  const establishmentType = entity?.establishmentType; // 'headquarters' or 'branch' (normalized)
  const establishmentTypeRaw = entity?.establishmentTypeRaw; // Original: 'Matriz', 'Filial'
  const cnpjRoot = entity?.cnpjRoot;
  const updatedAt = entity?.updatedAt;

  // Get company size (normalized)
  const companySize = entity?.companySize; // 'micro', 'small', 'medium', 'large'
  const companySizeRaw = entity?.companySizeRaw; // Original text

  // Get enrichment data (from combined enrichment: CEP, Geolocation, WhatsApp)
  const enrichment = entity?._enrichment || {};
  const cepEnrichment = enrichment.cep?.success ? enrichment.cep.data : null;
  const geoEnrichment = enrichment.geolocation?.success ? enrichment.geolocation.data : null;
  const whatsappEnrichment = enrichment.whatsapp?.success ? enrichment.whatsapp.data : null;

  // Build WhatsApp lookup map for phones (phone number -> hasWhatsapp)
  const whatsappMap = buildWhatsappMap(whatsappEnrichment);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [updateConfirmed, setUpdateConfirmed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createConfirmed, setCreateConfirmed] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  async function handleUpdateClick() {
    setIsUpdating(true);
    try {
      if (onUpdateEntity) {
        await onUpdateEntity(existingEntityId, entity);
      }
      setUpdateConfirmed(true);
    } catch (error) {
      console.error('[SearchedEntity] Update error:', error);
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleCreateConfirm() {
    if (selectedProfiles.length === 0) return;

    setIsCreating(true);
    try {
      // Always include 'search' profile plus user selections (schema enum uses 'search')
      const profiles = ['search', ...selectedProfiles];

      if (onCreateEntity) {
        await onCreateEntity(entity, profiles);
      }
      setCreateConfirmed(true);
      setCreatePopoverOpen(false);
    } catch (error) {
      console.error('[SearchedEntity] Create error:', error);
    } finally {
      setIsCreating(false);
    }
  }

  function handleProfileChange(checkedValues) {
    setSelectedProfiles(checkedValues);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      {/* Natural language intro with typing effect */}
      <div style={{ marginBottom: 20, lineHeight: 1.6, fontSize: 15 }}>
        <TypingText
          text={naturalIntro}
          speed={10}
          onComplete={onIntroComplete}
          scrollContainerRef={chatContainerRef}
          style={{ color: 'var(--ant-color-text)' }}
        />
      </div>

      {/* Data Freshness Alert - shows after intro if data age is known */}
      {introTypingComplete && searchDataAge !== null && searchDataAge !== undefined && (
        <div className="fade-in">
          <DataFreshnessAlert dataAgeMonths={searchDataAge} showRefreshButton={false} />
        </div>
      )}

      {/* Entity Header: Icon + Name/Type - shows after intro */}
      {introTypingComplete && tp.header && (
        <Flex gap={16} align="flex-start" style={{ marginBottom: 20 }} className="fade-in">
          {/* Entity type icon */}
          <Flex
            align="center"
            justify="center"
            style={{
              width: 64,
              height: 64,
              borderRadius: 8,
              backgroundColor: token.colorPrimaryBg,
              flexShrink: 0,
            }}
          >
            {isCompany ? (
              <ShopOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            ) : (
              <UserOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            )}
          </Flex>

          {/* Name and type */}
          <Flex vertical justify="center" style={{ minHeight: 64 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {toCapitalCase(entity?.name || entity?.fullName || '')}
            </div>
            {entity?.name && entity?.fullName && entity?.name !== entity?.fullName && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                {toCapitalCase(entity.fullName)}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {/* Basic Data Section */}
      {introTypingComplete && tp.basicData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['basic-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'basic-data',
              label: (
                <Flex align="center" gap={8}>
                  <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('basic_data')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={isMobile ? 1 : 2}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                >
                  {/* LINHA 1: CNPJ (com tag) | Situação */}
                  {cnpjDoc?.value && (
                    <Descriptions.Item label={translateX('cnpj')}>
                      <Flex align="center" gap={8}>
                        {formatCnpj(cnpjDoc.value)}
                        {(establishmentType || establishmentTypeRaw) && (
                          <Tag
                            color={
                              isHeadquarters(establishmentType) || establishmentTypeRaw === 'Matriz'
                                ? 'success'
                                : 'processing'
                            }
                            style={{ marginLeft: 4 }}
                          >
                            {establishmentTypeRaw ||
                              translateX(ESTABLISHMENT_TYPE_LABELS[establishmentType]) ||
                              toCapitalCase(establishmentType)}
                          </Tag>
                        )}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {cpfDoc?.value && !cnpjDoc?.value && (
                    <Descriptions.Item label={translateX('cpf')}>
                      {formatCpf(cpfDoc.value)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label={translateX('status_short')}
                    span={!cnpjDoc?.value && !cpfDoc?.value && !isMobile ? 2 : 1}
                  >
                    {registrationStatus ? (
                      <Flex vertical gap={2}>
                        <Flex align="center" gap={6}>
                          {isActive ? (
                            <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                          )}
                          <span>
                            {registrationStatus.statusRaw ||
                              registrationStatus.statusText ||
                              translateX(REGISTRATION_STATUS_LABELS[registrationStatus.status]) ||
                              toCapitalCase(registrationStatus.status) ||
                              '-'}
                          </span>
                        </Flex>
                        {registrationStatus.date && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {translateX('in_date')}:{' '}
                            {new Date(registrationStatus.date).toLocaleDateString('pt-BR')}
                          </Text>
                        )}
                      </Flex>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>

                  {/* LINHA 2: Natureza (span 2 on desktop) */}
                  <Descriptions.Item label={translateX('nature_short')} span={isMobile ? 1 : 2}>
                    {formatLegalNature(entity?.legalNature, entity?.legalNatureCode)}
                  </Descriptions.Item>

                  {/* LINHA 3: Porte | Capital */}
                  <Descriptions.Item label={translateX('size_short')}>
                    {companySize
                      ? translateX(`company_size_${companySize}`)
                      : companySizeRaw ||
                        (entity?.size && typeof entity.size === 'object'
                          ? entity.size.description || entity.size.code
                          : entity?.size
                            ? toCapitalCase(entity.size)
                            : null) ||
                        '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translateX('capital_short')}>
                    {entity?.shareCapital !== undefined && entity?.shareCapital !== null
                      ? formatPrice(entity.shareCapital)
                      : '-'}
                  </Descriptions.Item>

                  {/* LINHA 4: Fundação | Atualização */}
                  <Descriptions.Item label={translateX('foundation_short')}>
                    {entity?.foundedAt
                      ? new Date(entity.foundedAt).toLocaleDateString('pt-BR')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translateX('update_short')}>
                    {updatedAt ? new Date(updatedAt).toLocaleDateString('pt-BR') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Contact Channels Section */}
      {introTypingComplete &&
        tp.contact &&
        (phones.length > 0 || emails.length > 0 || faxes.length > 0) && (
          <Collapse
            className="fade-in collapsed-content-fit"
            collapsible="icon"
            activeKey={['contact-channels']}
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'contact-channels',
                label: (
                  <Flex align="center" gap={8}>
                    <PhoneOutlined style={{ color: token.colorPrimary }} />
                    <span style={{ fontWeight: 600 }}>{translateX('contact_channels')}</span>
                  </Flex>
                ),
                showArrow: false,
                children: (
                  <Descriptions
                    bordered
                    size="small"
                    column={1}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                  >
                    {/* LINHA 1: Telefones (todos juntos) */}
                    {(phones.length > 0 || faxes.length > 0) && (
                      <Descriptions.Item label={translateX('phones')}>
                        <Flex vertical gap={4}>
                          {[...phones, ...faxes].map((phone, idx) => (
                            <Flex key={idx} align="center">
                              {formatPhone(phone.value)}
                              {renderWhatsappStatus(phone.value, whatsappMap, token)}
                            </Flex>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    )}
                    {/* LINHA 2: Emails */}
                    {emails.length > 0 && (
                      <Descriptions.Item label={translateX('emails')}>
                        <Flex vertical gap={4}>
                          {emails.map((email, idx) => (
                            <div key={idx}>{email.value}</div>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                ),
              },
            ]}
          />
        )}

      {/* Address Section */}
      {introTypingComplete && tp.contact && primaryAddress && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['address']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'address',
              label: (
                <Flex align="center" gap={8}>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('address')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <>
                  <Descriptions
                    bordered
                    size="small"
                    column={1}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                  >
                    {/* LINHA 1: Endereço completo empilhado */}
                    <Descriptions.Item label={translateX('address')}>
                      <Flex vertical gap={2}>
                        {/* Logradouro, número */}
                        {(primaryAddress.address1 || primaryAddress.number) && (
                          <Text>
                            {[primaryAddress.address1, primaryAddress.number]
                              .filter(Boolean)
                              .join(', ')}
                          </Text>
                        )}
                        {/* Complemento */}
                        {primaryAddress.address2 && <Text>{primaryAddress.address2}</Text>}
                        {/* Bairro */}
                        {primaryAddress.neighborhood && <Text>{primaryAddress.neighborhood}</Text>}
                        {/* Cidade - Estado */}
                        {(primaryAddress.city || primaryAddress.region) && (
                          <Text>
                            {[primaryAddress.city, primaryAddress.region]
                              .filter(Boolean)
                              .join(' - ')}
                          </Text>
                        )}
                        {/* CEP */}
                        {primaryAddress.zip && <Text>{formatCep(primaryAddress.zip)}</Text>}
                      </Flex>
                    </Descriptions.Item>

                    {/* LINHA 2: Código IBGE */}
                    {(cepEnrichment?.ibgeCode || primaryAddress?.cityCode) && (
                      <Descriptions.Item label={translateX('ibge_code')}>
                        {cepEnrichment?.ibgeCode || primaryAddress?.cityCode}
                      </Descriptions.Item>
                    )}

                    {/* LINHA 3: Código de Área (DDD) */}
                    {cepEnrichment?.areaCode && (
                      <Descriptions.Item label={translateX('area_code')}>
                        {cepEnrichment.areaCode}
                      </Descriptions.Item>
                    )}

                    {/* LINHA 4: Coordenadas GPS */}
                    {geoEnrichment?.latitude && geoEnrichment?.longitude && (
                      <Descriptions.Item label={translateX('coordinates')}>
                        {geoEnrichment.latitude.toFixed(6)}, {geoEnrichment.longitude.toFixed(6)}
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {/* Embedded Map via Google Maps Static API */}
                  {geoEnrichment?.latitude != null && geoEnrichment?.longitude != null && (
                    <a
                      href={buildMapsLink(geoEnrichment.latitude, geoEnrichment.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        marginTop: 0,
                        borderRadius: '0 0 8px 8px',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${geoEnrichment.latitude},${geoEnrichment.longitude}&zoom=16&size=600x200&scale=2&maptype=roadmap&markers=color:red%7C${geoEnrichment.latitude},${geoEnrichment.longitude}&key=${import.meta.env.VITE_GOOGLE_APIKEY}`}
                        alt="Location map"
                        style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                        loading="lazy"
                      />
                    </a>
                  )}
                </>
              ),
            },
          ]}
        />
      )}

      {/* Economic Activities Section (Main + Secondary) */}
      {introTypingComplete && tp.activity && isCompany && mainActivity && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['economic-activities']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'economic-activities',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BankOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('economic_activities')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  <Descriptions.Item label={translateX('main_activity')}>
                    <Text>
                      {mainActivity.code} - {toCapitalCase(mainActivity.description)}
                    </Text>
                  </Descriptions.Item>
                  {secondaryActivities?.length > 0 && (
                    <Descriptions.Item label={translateX('secondary_activities')}>
                      <Flex vertical gap={2}>
                        {secondaryActivities.map((activity, index) => (
                          <Text key={index} type="secondary" style={{ fontSize: 12 }}>
                            {activity.code} - {toCapitalCase(activity.description)}
                          </Text>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Fiscal Data Section (includes State Registrations) */}
      {introTypingComplete && tp.fiscalData && isCompany && fiscalData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['fiscal-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'fiscal-data',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <AuditOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('fiscal_data')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {/* CNAE */}
                  {fiscalData.mainCnae && (
                    <Descriptions.Item label={translateX('cnae')}>
                      {fiscalData.mainCnae}
                    </Descriptions.Item>
                  )}
                  {/* Simples Nacional with CRT Tag */}
                  {fiscalData.isSimples !== undefined && (
                    <Descriptions.Item label="Simples Nacional">
                      <Flex align="center" gap={6}>
                        {fiscalData.isSimples ? (
                          <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                        )}
                        {fiscalData.isSimples ? translateX('yes') : translateX('no')}
                        {fiscalData.taxRegime && CRT_TAG_CONFIG[fiscalData.taxRegime] && (
                          <Tag
                            color={CRT_TAG_CONFIG[fiscalData.taxRegime].color}
                            style={{ marginLeft: 4 }}
                          >
                            {CRT_TAG_CONFIG[fiscalData.taxRegime].label}
                          </Tag>
                        )}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* Entrada no Simples */}
                  {fiscalData.simplesOptedAt && (
                    <Descriptions.Item label={translateX('simples_entry')}>
                      {new Date(fiscalData.simplesOptedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* Exclusão do Simples */}
                  {fiscalData.simplesExcludedAt && (
                    <Descriptions.Item label={translateX('simples_exclusion')}>
                      {new Date(fiscalData.simplesExcludedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* MEI */}
                  {fiscalData.isMei !== undefined && (
                    <Descriptions.Item label="MEI">
                      <Flex align="center" gap={6}>
                        {fiscalData.isMei ? (
                          <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                        )}
                        {fiscalData.isMei ? translateX('yes') : translateX('no')}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* Simples/MEI data update */}
                  {fiscalData.simplesUpdatedAt && (
                    <Descriptions.Item label={translateX('simples_updated_at')}>
                      {new Date(fiscalData.simplesUpdatedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* State Registrations (Inscrições Estaduais) */}
                  <Descriptions.Item label={translateX('state_registrations')}>
                    {stateRegistrations?.length > 0 ? (
                      <Flex vertical gap={8}>
                        {stateRegistrations.map((ie, index) => (
                          <Flex key={index} vertical gap={2}>
                            <Flex align="center" gap={6}>
                              {ie.isActive !== undefined &&
                                (ie.isActive ? (
                                  <CheckCircleOutlined
                                    style={{ color: 'var(--ant-color-success)', fontSize: 14 }}
                                  />
                                ) : (
                                  <CloseCircleOutlined
                                    style={{ color: 'var(--ant-color-error)', fontSize: 14 }}
                                  />
                                ))}
                              <Text>
                                {ie.state || ie.stateName} - {ie.stateRegistration}
                              </Text>
                            </Flex>
                            {ie.updatedAt && (
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 20 }}>
                                {translateX('updated')}:{' '}
                                {new Date(ie.updatedAt).toLocaleDateString('pt-BR')}
                              </Text>
                            )}
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      <Text>{translateX('exempt')}</Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Partners/Holders Section (for companies) */}
      {introTypingComplete && tp.partners && isCompany && entity?.holders?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['partners']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'partners',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <TeamOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('partners')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  <Descriptions.Item label={translateX('partners')}>
                    <Flex vertical gap={16}>
                      {entity.holders.slice(0, 10).map((holder, index) => (
                        <Flex key={index} vertical gap={2}>
                          {/* Name */}
                          <Text strong>{toCapitalCase(holder.name)}</Text>
                          {/* CPF (masked) */}
                          {holder.cpfMasked && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              CPF: {holder.cpfMasked}
                            </Text>
                          )}
                          {/* Entry date */}
                          {holder.sinceAt && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {translateX('since')}:{' '}
                              {new Date(holder.sinceAt).toLocaleDateString('pt-BR')}
                            </Text>
                          )}
                          {/* Age range */}
                          {(holder.ageRange || holder.ageRangeRaw) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {translateX('age_range')}:{' '}
                              {holder.ageRange
                                ? translateX(`age_range_${holder.ageRange}`)
                                : holder.ageRangeRaw}
                            </Text>
                          )}
                        </Flex>
                      ))}
                      {entity.holders.length > 10 && (
                        <Text type="secondary">
                          +{entity.holders.length - 10} {translateX('more_partners')}
                        </Text>
                      )}
                    </Flex>
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Meta info */}
      {introTypingComplete && tp.meta && (
        <div className="fade-in" style={{ marginTop: 16 }}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            wrap="wrap"
            gap={isMobile ? 12 : 16}
          >
            {/* Meta stats */}
            <Flex gap={isMobile ? 16 : 24} wrap="wrap" justify={isMobile ? 'center' : 'flex-start'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('engine')}:</span>{' '}
                {engine?.toUpperCase() || 'HOD'}
              </Text>
              {meta?.creditsUsed !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('credits_debited')}:</span>{' '}
                  {meta.creditsUsed}
                </Text>
              )}
              {creditsInfo?.availableCredits !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('current_balance')}:</span>{' '}
                  {creditsInfo.availableCredits}
                </Text>
              )}
            </Flex>

            {/* Action links */}
            <Flex gap={16} align="center" justify={isMobile ? 'center' : 'flex-end'}>
              {/* Update button - when entity exists */}
              {entityExists && existingEntityId && !updateConfirmed && (
                <a
                  onClick={handleUpdateClick}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: isUpdating ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: isUpdating ? 0.6 : 1,
                    marginTop: isMobile ? 16 : 0,
                  }}
                >
                  <SyncOutlined spin={isUpdating} />
                  {isUpdating ? translateX('updating') : translateX('update_record')}
                </a>
              )}

              {/* Create button with popover - when entity doesn't exist */}
              {!entityExists && !createConfirmed && (
                <Popover
                  open={createPopoverOpen}
                  onOpenChange={setCreatePopoverOpen}
                  trigger="click"
                  placement="topRight"
                  content={
                    <Flex vertical gap={12} style={{ minWidth: 200 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {translateX('select_profiles')}:
                      </Text>
                      <Checkbox.Group
                        value={selectedProfiles}
                        onChange={handleProfileChange}
                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                      >
                        {ENTITY_PROFILE_OPTIONS.map(opt => (
                          <Checkbox key={opt.value} value={opt.value}>
                            {translateX(opt.label)}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                      <Button
                        type="primary"
                        size="small"
                        loading={isCreating}
                        disabled={selectedProfiles.length === 0}
                        onClick={handleCreateConfirm}
                        style={{ marginTop: 4 }}
                      >
                        {translateX('confirm')}
                      </Button>
                    </Flex>
                  }
                >
                  <a
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginTop: isMobile ? 16 : 0,
                    }}
                  >
                    <PlusOutlined />
                    {translateX('add_record')}
                  </a>
                </Popover>
              )}
            </Flex>
          </Flex>
        </div>
      )}

      {/* Success Confirmation (Update or Create) */}
      {introTypingComplete && tp.editLink && (updateConfirmed || createConfirmed) && (
        <div className="fade-in" style={{ marginTop: 16 }}>
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            title={translateX(
              updateConfirmed ? 'record_updated_successfully' : 'record_created_successfully'
            )}
          />
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SearchedEntity;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Build WhatsApp lookup map for phones (phone number -> hasWhatsapp)
 */
function buildWhatsappMap(whatsappEnrichment) {
  const whatsappMap = {};
  if (whatsappEnrichment?.numbers) {
    whatsappEnrichment.numbers.forEach(n => {
      // Store both with and without country code for matching
      const cleanPhone = n.phoneNumber?.replace(/\D/g, '');
      if (cleanPhone) {
        whatsappMap[cleanPhone] = n.hasWhatsapp;
        // Also store without country code (55)
        if (cleanPhone.startsWith('55')) {
          whatsappMap[cleanPhone.substring(2)] = n.hasWhatsapp;
        }
      }
    });
  }
  return whatsappMap;
}

/**
 * Render WhatsApp status icon for a phone number
 */
function renderWhatsappStatus(phoneValue, whatsappMap, token) {
  const cleanPhone = phoneValue?.replace(/\D/g, '');
  if (!cleanPhone || Object.keys(whatsappMap).length === 0) return null;

  const hasWhatsapp = whatsappMap[cleanPhone];

  if (hasWhatsapp === true) {
    return (
      <Tooltip title={translateX('has_whatsapp')}>
        <WhatsAppOutlined style={{ color: '#25D366', marginLeft: 8 }} />
      </Tooltip>
    );
  } else if (hasWhatsapp === false) {
    return (
      <Tooltip title={translateX('no_whatsapp')}>
        <WhatsAppOutlined style={{ color: token.colorTextDisabled, marginLeft: 8 }} />
      </Tooltip>
    );
  }
  // undefined = validation not performed or skipped
  return null;
}

/**
 * Build Google Maps link from coordinates
 */
function buildMapsLink(lat, lng) {
  if (!lat || !lng) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

/**
 * Format legal nature with code
 * Handles both object format { code, description } and string format
 * Also checks for separate legalNatureCode field
 */
function formatLegalNature(legalNature, legalNatureCode) {
  if (!legalNature) return '-';

  // If legalNature is an object with code and description
  if (typeof legalNature === 'object' && legalNature !== null) {
    const code = legalNature.code || legalNatureCode;
    const description = legalNature.description;
    if (code && description) return `${code} - ${description}`;
    if (description) return description;
    if (code) return code;
    return '-';
  }

  // If legalNature is a string, check if we have a separate code
  if (typeof legalNature === 'string') {
    if (legalNatureCode) return `${legalNatureCode} - ${legalNature}`;
    return legalNature;
  }

  return '-';
}
